import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { useOrganization } from '../contexts/OrganizationContext';
import { useError } from '../contexts/ErrorContext';
import AddBusinessOwnerModal from './AddBusinessOwnerModal';
import { useNavigate } from 'react-router-dom';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import PlatformInput from './PlatformInput';
import LoadingScreen from './LoadingScreen';
import { useTranslation } from '../hooks/useTranslation';

function UserManagement() {
  const navigate = useNavigate();
  const { organizationId, organizationData, loading: orgLoading, userRole } = useOrganization();
  const { showErrorToast } = useError();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation('admin');

  // Security check - if not a business owner, redirect to orders page
  useEffect(() => {
    if (!orgLoading && userRole !== 'BusinessOwner') {
      navigate('/orders');
    }
  }, [userRole, orgLoading, navigate]);

  const userRoles = {
    BUSINESS_OWNER: 'BusinessOwner',
    SALESPERSON: 'Salesperson',
    ACCOUNTANT: 'Accountant',
    INVENTORY_MANAGER: 'InventoryManager'
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      user.fullName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  const fetchUsers = useCallback(async () => {
    if (!organizationId) return;
    
    try {
      // Security check - verify user is business owner before fetching data
      if (userRole !== 'BusinessOwner') {
        showErrorToast(t('errors.unauthorizedAccessToast'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      const usersRef = collection(db, `organizations/${organizationId}/users`);
      const querySnapshot = await getDocs(usersRef);
      
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({ id: doc.id, ...doc.data() });
      });
      
      setUsers(usersList);
      setLoading(false);
    } catch (err) {
      setError(err.message === 'Unauthorized access' ? t('errors.unauthorizedAccess') : t('errors.failedToFetchUsers'));
      setLoading(false);
      
      // If unauthorized, redirect to orders page
      if (err.message === 'Unauthorized access') {
        navigate('/orders');
      }
    }
  }, [organizationId, userRole, navigate]);

  useEffect(() => {
    // Only fetch users when organization context is fully loaded
    if (!orgLoading && organizationId) {
      fetchUsers();
    }
  }, [organizationId, orgLoading, fetchUsers]);

  const handleAccessToggle = async (userId, currentStatus) => {
    try {
      // Security check - verify user is business owner before updating
      if (userRole !== 'BusinessOwner') {
        showErrorToast(t('errors.unauthorizedAccessToast'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      const userRef = doc(db, `organizations/${organizationId}/users/${userId}`);
      await updateDoc(userRef, { approved: !currentStatus });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, approved: !currentStatus } : user
      ));
    } catch (err) {
      setError(err.message === 'Unauthorized access' ? t('errors.unauthorizedAction') : t('errors.failedToUpdateAccess'));
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      // Security check - verify user is business owner before updating
      if (userRole !== 'BusinessOwner') {
        showErrorToast(t('errors.unauthorizedAccessToast'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }

      const userRef = doc(db, `organizations/${organizationId}/users/${userId}`);
      await updateDoc(userRef, { role: newRole });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
    } catch (err) {
      setError(err.message === 'Unauthorized access' ? t('errors.unauthorizedAction') : t('errors.failedToUpdateRole'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!organizationId || !userId) return;
    if (!window.confirm(t('confirmations.deleteUser'))) return;
    try {
      // Delete from organization users subcollection
      await deleteDoc(doc(db, `organizations/${organizationId}/users/${userId}`));
      // Delete from root users collection
      await deleteDoc(doc(db, 'users', userId));
      // Update user count
      try {
        const { updateOrganizationUserCount } = await import('../services/userCountService');
        await updateOrganizationUserCount(organizationId);
      } catch (err) {
        // Failed to update organization user count - handled silently
      }
      // Remove from local state
      setUsers(users.filter(user => user.id !== userId));
    } catch (err) {
      setError(t('errors.failedToDeleteUser'));
    }
  };

  // Show loading state while either component is loading or organization context is loading
  if (loading || orgLoading) return <LoadingScreen message={t('loading.users')} variant="fullscreen" />;
  if (error) return <div className="error-message">{error}</div>;

  // Final security check before rendering
  if (userRole !== 'BusinessOwner') {
    return null;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <PlatformText variant="h2">{t('title')}</PlatformText>
        <PlatformButton 
          onClick={() => setIsModalOpen(true)}
          className="add-owner-button"
        >
          {t('actions.addBusinessOwner')}
        </PlatformButton>
      </div>

      {/* Search Bar */}
      <div className="user-search-container">
        <PlatformInput
          type="text"
          placeholder={t('actions.searchUsers')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="user-search-input"
        />
      </div>

      <div className="users-list">
        {filteredUsers.map(user => (
          <div key={user.id} className="user-card">
            <div className="user-card-content">
              <div className="user-info">
                <PlatformText variant="h3" className="user-name" align="center">{user.fullName}</PlatformText>
                <PlatformText variant="body" className="user-email" align="center">
                  {t('fields.email')}: {user.email}
                </PlatformText>
                <PlatformText variant="body" className="user-role" align="center">
                  {t('fields.currentRole')}: {user.role}
                </PlatformText>
                <span className={`status-badge ${user.approved ? 'approved' : 'revoked'}`}>
                  {user.approved ? t('status.approved') : t('status.accessRevoked')}
                </span>
              </div>
              {user.role !== userRoles.BUSINESS_OWNER && (
                <div className="user-actions-col">
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="role-select full-width"
                  >
                    <option value={userRoles.SALESPERSON}>{t('roles.salesperson')}</option>
                    <option value={userRoles.ACCOUNTANT}>{t('roles.accountant')}</option>
                    <option value={userRoles.INVENTORY_MANAGER}>{t('roles.inventoryManager')}</option>
                  </select>
                  <PlatformButton
                    onClick={() => handleAccessToggle(user.id, user.approved)}
                    className={`revoke-access-btn ${user.approved ? 'revoke' : 'approve'} full-width`}
                  >
                    {user.approved ? t('actions.revokeAccess') : t('actions.approveAccess')}
                  </PlatformButton>
                  <PlatformButton
                    onClick={() => handleDeleteUser(user.id)}
                    className="delete-user-btn full-width"
                  >
                    {t('actions.deleteUser')}
                  </PlatformButton>
                </div>
              )}
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && (
          <div className="no-users-message">
            {searchQuery.trim() ? t('messages.noSearchResults') : t('messages.noUsers')}
          </div>
        )}
      </div>

      <AddBusinessOwnerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchUsers(); // Refresh the users list after adding a new business owner
        }}
        organizationId={organizationId}
        organizationCode={organizationData?.organizationCode}
      />
    </div>
  );
}

export default UserManagement; 